import { z } from "zod";

/** Columns the API can sort on — all backed by real DB columns. */
export const PROCEDURE_SORT_FIELDS = [
  "identifier",
  "claimNumber",
  "court",
  "nextHearing",
  "courtFillingDate",
  "deliberationDate",
  "decisionDate",
  "syncedAt",
] as const;

export type ProcedureSortField = (typeof PROCEDURE_SORT_FIELDS)[number];

/** Offered page sizes. The API caps at 500, so all of these are safe. */
export const PROCEDURE_PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Fifty rather than twenty-five: a court day routinely holds more than 25
 * hearings, and splitting one across two pages breaks the month bands and the
 * reading flow far more than the extra rows cost.
 */
export const DEFAULT_PAGE_SIZE = 50;

/** Soonest hearing first — what the team wants to see when landing on the page. */
export const DEFAULT_SORT_BY: ProcedureSortField = "nextHearing";
export const DEFAULT_SORT_DIR = "asc" as const;

/**
 * Filters, sort and page live in the URL so a filtered table is shareable and
 * survives a back-navigation — the convention already used by /tasks and
 * /reporting. Every key is optional: absent means "no restriction", so no
 * `all`-style sentinel ever reaches the address bar or the UI.
 */
export const proceduresSearchSchema = z.object({
  /** Absent means enrolled-only (the default); pass false to include the rest. */
  enrolled: z.boolean().optional().catch(undefined),
  /** Absent hides cases closed over 30 days ago; pass true to bring them back. */
  closed: z.boolean().optional().catch(undefined),
  /** Multi-select — an empty array is normalised away so the URL stays clean. */
  courts: z.array(z.number()).optional().catch(undefined),
  airlines: z.array(z.string()).optional().catch(undefined),
  admins: z.array(z.string()).optional().catch(undefined),
  /** Agencies the files came from — the claim's source tenant. */
  tenants: z.array(z.string()).optional().catch(undefined),
  positions: z.array(z.string()).optional().catch(undefined),
  type: z.enum(["PROCEDURE_CLASSIQUE", "ESCP"]).optional().catch(undefined),

  hearingTypes: z.array(z.string()).optional().catch(undefined),
  /** Procedure whose panel is open, so the rail survives a refresh or a share. */
  affaire: z.string().optional().catch(undefined),
  /** Absent keeps the calendar rail closed; `true` opens it. */
  calendar: z.boolean().optional().catch(undefined),
  hearing: z.enum(["upcoming", "none"]).optional().catch(undefined),
  /** Day picked in the calendar, as `yyyy-MM-dd`. */
  day: z.string().optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  sortBy: z.enum(PROCEDURE_SORT_FIELDS).optional().catch(undefined),
  sortDir: z.enum(["asc", "desc"]).optional().catch(undefined),
  page: z.number().optional().catch(undefined),
  /** Absent means the default size, so the URL only carries a deliberate choice. */
  pageSize: z.number().optional().catch(undefined),
});

export type ProceduresSearch = z.infer<typeof proceduresSearchSchema>;

/** An unknown value in the URL falls back to the default rather than reaching the API. */
export function pageSizeOf(search: ProceduresSearch): number {
  const size = search.pageSize;
  return size !== undefined && PROCEDURE_PAGE_SIZES.includes(size as (typeof PROCEDURE_PAGE_SIZES)[number])
    ? size
    : DEFAULT_PAGE_SIZE;
}

/** The toggle ships checked, so an absent value means "enrolled only". */
export function isEnrolledOnly(search: ProceduresSearch): boolean {
  return search.enrolled !== false;
}

/** Likewise: long-closed cases are hidden until the toggle is unchecked. */
export function hidesLongClosed(search: ProceduresSearch): boolean {
  return search.closed !== true;
}

/**
 * The calendar ships closed — the table is the page, the calendar an aside — so
 * opening it is what gets recorded.
 *
 * Independent of the `day` filter on purpose: closing the calendar keeps the
 * selected day filtering, and the active-filter chip is what makes it visible.
 */
export function showsCalendar(search: ProceduresSearch): boolean {
  return search.calendar === true;
}

function toDate(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Translate the URL state into the query the SDK expects. */
export function toListParams(search: ProceduresSearch) {
  return {
    ...(isEnrolledOnly(search) ? { enrolled: true } : {}),
    ...(hidesLongClosed(search) ? {} : { includeLongClosed: true }),
    ...(search.courts?.length ? { courtIds: search.courts } : {}),
    ...(search.airlines?.length ? { airlineIds: search.airlines } : {}),
    ...(search.admins?.length ? { adminIds: search.admins } : {}),
    ...(search.tenants?.length ? { tenantIds: search.tenants } : {}),
    ...(search.type ? { procedureType: search.type } : {}),
    ...(search.positions?.length ? { positions: search.positions } : {}),
    ...(search.hearingTypes?.length ? { hearingTypeNames: search.hearingTypes } : {}),
    ...(search.hearing ? { hearingScope: search.hearing } : {}),
    ...(toDate(search.day) ? { hearingOn: toDate(search.day) } : {}),
    ...(toDate(search.from) ? { enrolledFrom: toDate(search.from) } : {}),
    ...(toDate(search.to) ? { enrolledTo: toDate(search.to) } : {}),
    ...(search.q?.trim() ? { search: search.q.trim() } : {}),
    sortBy: search.sortBy ?? DEFAULT_SORT_BY,
    sortDir: search.sortDir ?? DEFAULT_SORT_DIR,
    page: search.page ?? 1,
    pageSize: pageSizeOf(search),
  };
}

/** Keys cleared by « Tout effacer » — everything but the sort and the page. */
export const FILTER_KEYS = [
  "enrolled",
  "closed",
  "courts",
  "airlines",
  "admins",
  "tenants",
  "type",

  "positions",
  "hearingTypes",
  "hearing",
  "day",
  "from",
  "to",
  "q",
] as const satisfies ReadonlyArray<keyof ProceduresSearch>;

export function clearedFilters(): Partial<ProceduresSearch> {
  return Object.fromEntries(FILTER_KEYS.map((key) => [key, undefined]));
}

/**
 * Which patches invalidate the current page. Narrowing the result set or
 * reordering it does — page 4 of the old list means nothing in the new one.
 *
 * Opening an affaire, toggling the calendar or picking a page does *not*: those
 * leave the list untouched, and resetting on them used to throw you back to page
 * 1 the moment you clicked a row you had paginated to.
 */
export function resetsPagination(patch: Partial<ProceduresSearch>): boolean {
  const invalidating: ReadonlyArray<keyof ProceduresSearch> = [
    ...FILTER_KEYS,
    "sortBy",
    "sortDir",
    "pageSize",
  ];

  return invalidating.some((key) => key in patch);
}

/** Toggle one value of a multi-select, dropping the key when it empties out. */
export function toggleInList<T extends number | string>(current: T[] | undefined, value: T): T[] | undefined {
  const next = (current ?? []).includes(value)
    ? (current ?? []).filter((entry) => entry !== value)
    : [...(current ?? []), value];

  return next.length > 0 ? next : undefined;
}
